import {
  artifactCardSchema,
  automationCardSchema,
  automationSetupCardSchema,
  clarificationCardSchema,
  confirmationCardSchema,
  connectCardSchema,
  integrationAccessRequestCardSchema,
} from '@/schemas/contracts/cards'

function resultPayload(event) {
  return event?.result ?? event?.payload ?? null
}

function typedCard(type, data, event) {
  return event?.attempt_id
    ? { type, ...data, attemptId: event.attempt_id }
    : { type, ...data }
}

// A `tool` event whose arguments carry a browser_preview rides the live,
// read-only stream URL of an escalated browser run (no new SSE event type).
export function toBrowserPreviewBlock(event) {
  const args = event?.arguments
  if (!args || typeof args !== 'object' || args.kind !== 'browser_preview') return null
  const streamingUrl = args.streaming_url
  if (typeof streamingUrl !== 'string' || !streamingUrl) return null
  return { type: 'browser_preview', streamingUrl }
}

export function toTypedBlock(event) {
  const payload = resultPayload(event)
  if (!payload || typeof payload !== 'object') return null

  const artifact = artifactCardSchema.safeParse(payload)
  if (artifact.success) return typedCard('artifact_card', artifact.data, event)

  const confirmation = confirmationCardSchema.safeParse(payload)
  if (confirmation.success) return typedCard('confirmation_card', confirmation.data, event)

  // Before connect, like the two above: its required fields (workflow_id, cron)
  // are carried by no other card, so the order is belt-and-braces rather than
  // load-bearing.
  const automation = automationCardSchema.safeParse(payload)
  if (automation.success) return typedCard('automation_card', automation.data, event)
  const automationSetup = automationSetupCardSchema.safeParse(payload)
  if (automationSetup.success) return typedCard('automation_setup_card', automationSetup.data, event)

  // This card carries an explicit discriminant and must precede the deliberately
  // loose connect-card schema or it could be rendered as a generic connection.
  const access = integrationAccessRequestCardSchema.safeParse(payload)
  if (access.success) return typedCard('integration_access_request', access.data, event)

  // Second to last: its only required field is nullable, so it is the loosest
  // shape here and would otherwise swallow payloads meant for the schemas above.
  const connect = connectCardSchema.safeParse(payload)
  if (connect.success) return typedCard('connect_card', connect.data, event)

  // After connect rather than before it only because connect must stay adjacent
  // to the comment explaining why it is late. This one is genuinely narrow —
  // nothing else carries `questions` — so its position is not load-bearing, but
  // appending keeps every existing card's parse order untouched.
  const clarification = clarificationCardSchema.safeParse(payload)
  if (clarification.success) return typedCard('clarification_card', clarification.data, event)

  return null
}
